/**
 * Оболочка и роутинг · Ф2.2
 *
 * Экранов пять, навигация — плавающий «остров» с центральной «Учить» (Минимал).
 * Роутинг внутренний (без адресной строки: у Mini App её нет), системная кнопка
 * «назад» Telegram уводит в кабинет.
 */

import { setBackButton, tap } from "./tg";
import { state, type ScreenName } from "./state";
import { enterNav, enterScreen } from "./anim";

export type Renderer = (host: HTMLElement) => void;

interface NavItem { name: ScreenName; label: string; icon: string; center?: boolean }

const ICON = {
  cabinet: '<path d="M4 11l8-7 8 7"/><path d="M6 10v9h4v-5h4v5h4v-9"/>',
  stats: '<path d="M4 20V6"/><path d="M4 20h16"/><path d="M8 20v-6"/><path d="M12 20v-9"/><path d="M16 20v-4"/>',
  session: '<path d="M4 5h6a2 2 0 012 2v12a3 3 0 00-3-3H4z"/><path d="M20 5h-6a2 2 0 00-2 2v12a3 3 0 013-3h5z"/>',
  awards: '<circle cx="12" cy="9" r="5"/><path d="M9 13l-2 8 5-3 5 3-2-8"/>',
  settings: '<circle cx="12" cy="12" r="3.2"/><path d="M12 3.2v2.4M12 18.4v2.4M4.6 12H2.2M21.8 12h-2.4' +
            'M6.3 6.3L4.6 4.6M19.4 19.4l-1.7-1.7M17.7 6.3l1.7-1.7M4.6 19.4l1.7-1.7"/>',
};

const NAV: NavItem[] = [
  { name: "cabinet", label: "Кабинет", icon: ICON.cabinet },
  { name: "stats", label: "Статистика", icon: ICON.stats },
  { name: "session", label: "Учить", icon: ICON.session, center: true },
  { name: "awards", label: "Награды", icon: ICON.awards },
  { name: "settings", label: "Настройки", icon: ICON.settings },
];

const renderers = new Map<ScreenName, Renderer>();
const hosts = new Map<ScreenName, HTMLElement>();
let scrollEl: HTMLElement | null = null;
let onNavigate: ((name: ScreenName) => void) | null = null;

/** Собрать оболочку: скролл-контейнер с экранами + навигация-остров. */
export function mountShell(defs: { name: ScreenName; render: Renderer }[],
                           navigated?: (name: ScreenName) => void): void {
  onNavigate = navigated ?? null;
  const shell = document.getElementById("shell");
  if (!shell) throw new Error("Нет корневого элемента #shell");

  shell.innerHTML =
    '<main class="scroll" id="scroll">' +
      '<div class="wrap" id="wrap">' +
        defs.map((d) => `<section class="screen" data-screen="${d.name}"></section>`).join("") +
      "</div>" +
    "</main>" +
    '<nav class="nav" id="nav" aria-label="Навигация">' +
      NAV.map((n) =>
        `<button class="navbtn${n.center ? " center" : ""}" data-go="${n.name}" type="button">` +
        (n.center
          ? `<span class="cbtn"><svg viewBox="0 0 24 24">${n.icon}</svg></span>`
          : `<svg viewBox="0 0 24 24">${n.icon}</svg>`) +
        `<span class="nl">${n.label}</span></button>`).join("") +
    "</nav>";

  scrollEl = document.getElementById("scroll");
  for (const d of defs) {
    const host = shell.querySelector<HTMLElement>(`[data-screen="${d.name}"]`);
    if (!host) continue;
    hosts.set(d.name, host);
    renderers.set(d.name, d.render);
  }

  const nav = document.getElementById("nav");
  if (nav) enterNav(nav);

  // Один обработчик на всё: любая кнопка с data-go ведёт на свой экран.
  shell.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-go]");
    if (!btn) return;
    tap(btn);
    go(btn.dataset.go as ScreenName);
  });
}

/** Перейти на экран (и перерисовать его свежими данными). */
export function go(name: ScreenName): void {
  if (!hosts.has(name)) return;
  const same = state.screen === name && hosts.get(name)?.classList.contains("on");
  state.screen = name;
  render(name);
  for (const [key, host] of hosts) host.classList.toggle("on", key === name);
  const shown = hosts.get(name);
  if (shown && !same) enterScreen(shown);
  document.querySelectorAll<HTMLElement>("#nav .navbtn").forEach((b) => {
    b.setAttribute("aria-current", b.dataset.go === name ? "true" : "false");
  });
  if (scrollEl) scrollEl.scrollTop = 0;
  setBackButton(name !== "cabinet", () => go("cabinet"));
  onNavigate?.(name);
}

/** Перерисовать экран на месте (например, когда доехали свежие цифры). */
export function render(name: ScreenName): void {
  const host = hosts.get(name);
  const renderer = renderers.get(name);
  if (host && renderer) renderer(host);
}

/** Перерисовать тот экран, который сейчас открыт. */
export function renderCurrent(): void {
  render(state.screen);
}
